import React from "react";
import Image from "next/image";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Typography from "@mui/material/Typography";
import { useRouter } from "next/router";

import SVG from "@/assets/svg";
import CostumeButton from "@/component/button";
import { Visit } from "@/interfaces/IServiceUser";
import {
  TaskManagementResponse,
  TaskStatus,
} from "@/interfaces/ITaskManagement";
import { useAppSelector } from "@/redux/hooks";
import { $public } from "@/services";
import { getSheduleVisitById } from "@/services/service-users/schedule-visit";
import { updateTask } from "@/services/task-management";
import { getSanitizedHtml, getTaskLiveStatus } from "@/utils/helpers";
import { getVisitTaskContext } from "@/utils/visitTaskContext";
import logger from "@/runtime-utils/observability/logger";
import DialogueBox from "../dialogueBox";
import AddTaskData from "../dialogueBox/task-data";
import ListView from "../list";
import { DrawerStyled } from "./drawerStyled";

type Props = {
  tasks: TaskManagementResponse[];
  loading: boolean;
  setTasks: React.Dispatch<React.SetStateAction<TaskManagementResponse[]>>;
};
const STATUS_OPTIONS: { value: TaskStatus; label: string }[] = [
  {
    value: "completed",
    label: "Completed",
  },
  {
    value: "not needed",
    label: "Not Needed",
  },
];

function Tasks({ loading, tasks, setTasks }: Props) {
  const { selectedVisit } = useAppSelector((state) => state.roster);
  const { user } = useAppSelector((state) => state.user);
  const router = useRouter();
  const visit = selectedVisit?.extendedProps as Visit | undefined;
  const visitTaskContext = getVisitTaskContext(visit);

  const date = selectedVisit?.startStr?.split("T")?.[0] as string;

  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
  const [openModal, setOpenModal] = React.useState(false);
  const [selectedTaskId, setSelectedTaskId] = React.useState<string | null>(
    null
  );
  const [selectedDate, setSelectedDate] = React.useState<string>("");

  const handleClose = () => {
    setSelectedTaskId(null);
    setOpenModal(false);
    setAnchorEl(null);
  };

  const handleView = () => {
    setAnchorEl(null);
    setOpenModal(true);
  };

  const [selectedRowId, setSelectedRowId] = React.useState<string | null>(null);
  const [statusLoadingId, setStatusLoadingId] = React.useState<string>("");
  const [statusAnchorEl, setStatusAnchorEl] =
    React.useState<null | HTMLElement>(null);

  const statusOpen = Boolean(statusAnchorEl);

  const handleStatusClick = (
    event: React.MouseEvent<HTMLSpanElement>,
    id: string
  ) => {
    event.stopPropagation();
    setStatusAnchorEl(event.currentTarget);
    setSelectedRowId(id);
  };

  const handleStatusClose = () => {
    setStatusAnchorEl(null);
    setSelectedRowId(null);
  };

  const handleStatusChange = async (
    value: TaskStatus,
    oldStatus: TaskManagementResponse["status"]
  ) => {
    try {
      // return;
      if (!selectedRowId) return;
      setStatusLoadingId(selectedRowId);
      handleStatusClose();

      const body = {
        shouldAddDailyNote: true,
        date: selectedDate,
        createdBy: {
          id: user?._id,
          name: user?.name,
        },
        status: {
          [selectedDate]: value,
        },
        visitId: selectedVisit?.extendedProps?._id,
      };

      const response = await updateTask(selectedRowId, body);
      if (response.success) {
        const updatedList = tasks.map((item) => {
          if (item._id === selectedRowId) {
            return {
              ...item,
              status: {
                ...oldStatus,
                [date]: value,
              },
            };
          }
          return item;
        });

        setTasks(updatedList);

        // fetch this particular visit again to update the notes in roster
        const newVisit = await getSheduleVisitById(
          selectedVisit?.extendedProps?._id as string
        );
        if (newVisit.success) {
          if (newVisit.data?.notes) {
            selectedVisit?.setExtendedProp("notes", newVisit.data?.notes);
          }
        }
      }
    } catch (error) {
      logger.error("error", error);
    } finally {
      setStatusLoadingId("");
      setSelectedRowId(null);
    }
  };

  return (
    <DrawerStyled>
      <Box className="Activities">
        {visitTaskContext.serviceUser && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              padding: "10px 5px 0 0",
            }}
          >
            <CostumeButton
              onClick={() =>
                router.push({
                  pathname: "/tasks-management/task-add",
                  query: visitTaskContext,
                })
              }
              className="costume"
            >
              Add task
              <SVG.Addfile />
            </CostumeButton>
          </Box>
        )}
        <ListView
          data={tasks}
          loading={loading}
          renderItem={(item, index) => {
            const status = getTaskLiveStatus(item, date);
            return (
              <Box className="Detail">
                <Box className="planDetail">
                  {/* <SVG.Role width="50px" /> */}
                  <Box className="task">
                    <Box sx={{ position: "relative" }}>
                      {item?.tasks
                        ?.slice(0, 1)
                        .map((task: any, index: number) => (
                          <Box key={index} className="daily-notes">
                            <Image
                              src={$public(task.logo)}
                              alt={""}
                              width={25}
                              height={25}
                            />
                          </Box>
                        ))}
                    </Box>
                  </Box>
                  <Box className="planned">
                    <p className="username">{item.createdBy?.name}</p>
                    <Typography
                      variant="h3"
                      sx={{
                        "& p": {
                          margin: 0,
                          padding: 0,
                        },
                      }}
                      className="Visit"
                    >
                      <SVG.Check style={{ width: "20px", margin: "0px 4px" }} />{" "}
                      <p
                        style={{
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          width: "324px",
                          textOverflow: " ellipsis",
                        }}
                      >
                        {item?.tasks?.length > 2
                          ? `${item?.tasks[0]?.title} and more..`
                          : item?.tasks
                              .map((task: any) => task?.title)
                              .join(", ")}
                      </p>
                    </Typography>
                    <Box className="user-Status">
                      <Box>
                        <p
                          className="user-detail"
                          style={{
                            fontSize: "12px",
                            color: "#000000",
                            fontWeight: 500,
                          }}
                          dangerouslySetInnerHTML={{
                            __html: getSanitizedHtml(
                              item.description?.replaceAll("<p><br></p>", "")
                            ),
                          }}
                        />
                      </Box>
                      <Box>
                        <span
                          id={`status-open-${index}`}
                          onClick={(e) => {
                            handleStatusClick(e, item._id);
                            setSelectedDate(date);
                          }}
                          className={`status ${
                            status === "completed"
                              ? "complete"
                              : status === "overdue"
                                ? "overdue"
                                : status === "pending"
                                  ? "pending"
                                  : !status
                                    ? "pending"
                                    : status === "skipped" ||
                                        status === "not needed"
                                      ? "skipped"
                                      : "defaultClass"
                          }`}
                        >
                          {status || "pending"}
                          {statusLoadingId === item._id ? (
                            <CircularProgress
                              size={15}
                              style={{
                                marginLeft: "10px",
                              }}
                            />
                          ) : (
                            <SVG.DownArrow
                              width="10px"
                              style={{
                                paddingLeft: "10px",
                              }}
                            />
                          )}
                        </span>

                        <Menu
                          id={`status-menu-${index}`}
                          anchorEl={statusAnchorEl}
                          open={statusOpen && selectedRowId === item._id}
                          onClose={(e: React.MouseEvent<HTMLSpanElement>) => {
                            e.stopPropagation();
                            handleStatusClose();
                          }}
                          slotProps={{
                            list: {
                              "aria-labelledby": `status-open-${index}`,
                            },
                          }}
                        >
                          {STATUS_OPTIONS.map((option, index1) => (
                            <MenuItem
                              key={index1}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleStatusChange(option.value, item.status);
                              }}
                            >
                              {option.label}
                            </MenuItem>
                          ))}
                        </Menu>
                      </Box>
                    </Box>
                  </Box>
                  <Box>
                    <SVG.DotsHorizontal
                      width="20px"
                      onClick={(e: React.MouseEvent<HTMLElement>) => {
                        setSelectedTaskId(item._id);
                        setAnchorEl(e.currentTarget);
                      }}
                    />
                  </Box>
                </Box>
              </Box>
            );
          }}
        />
      </Box>
      <Menu
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
      >
        <MenuItem onClick={handleView}>View Details</MenuItem>
      </Menu>
      <DialogueBox onClose={handleClose} open={openModal}>
        <>
          <AddTaskData onClose={handleClose} id={selectedTaskId} />
        </>
      </DialogueBox>
    </DrawerStyled>
  );
}

export default Tasks;
